import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Map as MapIcon, Navigation, Pause, Play, RefreshCw, ShieldAlert, Video } from 'lucide-react'
import { useGetLiveFleet } from '../hooks/backend/fleet'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Label } from '../lib/shadcn/label'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { LiveFleetMap } from '../components/fleet/LiveFleetMap'
import { CameraTile } from '../components/fleet/CameraTile'
import { IDLE_PAUSE_MS, canStream } from '../utils/liveCamera'
import {
  LIVE_STATES,
  LIVE_STATE_LABEL,
  liveTone,
  sastTime,
  statusLine,
  type LiveFleetResult,
  type LiveFleetRow,
  type LiveState,
} from '../utils/liveFleet'

// The database refreshes from Cartrack once a minute; half that here means a
// change reaches the screen within about a minute and a half of Cartrack.
const REFRESH_MS = 30_000

/**
 * Live Fleet: where each of my cars is right now, from Cartrack.
 *
 * Opens on the signed-in manager's own cars, by cockpit_access.person_id, with
 * a switch to every car for whoever is covering. Every state and every "minutes
 * ago" is decided in v_vehicle_live_status (db/migrations/0366), so this page
 * and anything else that reads the view cannot disagree about which car is
 * moving.
 *
 * A car with no position is listed, never hidden and never pinned at a default
 * spot: "No tracker" and "No signal" are things a manager has to act on, and a
 * map that quietly drops them looks like a fleet with nothing wrong.
 *
 * Cameras (migration 0367). The Cameras view plays one live camera per car the
 * manager may watch, automatically and with the sound off; sound is one tile at
 * a time and only when he turns it on. Every stream is recorded against the
 * person who opened it. Streams pause when the tab is hidden and after ten
 * minutes without a touch, because a watched camera spends about 270 MB an hour
 * of the car's own data.
 *
 * Otherwise read only. Immobilising a car or messaging a driver from here would
 * be a write with consequences for a person, and belongs behind its own
 * confirmation, not beside a map pin.
 */
export default function LiveFleet() {
  const { loading: accessLoading, canView } = useCapabilities()
  const [scope, setScope] = useState<'mine' | 'all'>('mine')
  const [search, setSearch] = useState('')
  const [stateFilter, setStateFilter] = useState<LiveState | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [view, setView] = useState<'map' | 'cameras'>('map')
  const [soundVehicle, setSoundVehicle] = useState<string | null>(null)
  const [largeVehicle, setLargeVehicle] = useState<string | null>(null)
  const [tabVisible, setTabVisible] = useState(() => document.visibilityState === 'visible')
  const [idlePaused, setIdlePaused] = useState(false)
  const [userPaused, setUserPaused] = useState(false)
  const lastTouch = useRef(Date.now())

  // Hidden tab: stop every stream. Back again: they restart on their own.
  useEffect(() => {
    const on = () => setTabVisible(document.visibilityState === 'visible')
    // Read again now: a page opened in a background tab and brought forward
    // before this listener existed would otherwise stay paused.
    on()
    document.addEventListener('visibilitychange', on)
    return () => document.removeEventListener('visibilitychange', on)
  }, [])

  // Ten minutes without a touch pauses the cameras, to spare the cars' data.
  useEffect(() => {
    if (view !== 'cameras') return
    const touch = () => {
      lastTouch.current = Date.now()
    }
    lastTouch.current = Date.now()
    window.addEventListener('pointerdown', touch)
    window.addEventListener('keydown', touch)
    const id = window.setInterval(() => {
      if (Date.now() - lastTouch.current > IDLE_PAUSE_MS) setIdlePaused(true)
    }, 15_000)
    return () => {
      window.removeEventListener('pointerdown', touch)
      window.removeEventListener('keydown', touch)
      window.clearInterval(id)
    }
  }, [view])

  const feed = useGetLiveFleet<LiveFleetResult>()
  const trigger = feed.trigger
  const load = useCallback(() => {
    trigger({ scope }, { skipCache: true })
  }, [trigger, scope])

  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  const loadRef = useRef(load)
  loadRef.current = load
  useEffect(() => {
    if (!canView) return
    const tick = () => {
      if (document.visibilityState === 'visible') loadRef.current()
    }
    const id = window.setInterval(tick, REFRESH_MS)
    document.addEventListener('visibilitychange', tick)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [canView])

  const result = feed.data ?? null

  // Someone who manages no car (Leduma, Xolile, Musa and Yolisa on 30 September)
  // would otherwise open on an empty page. Switch them to every car, once, and
  // leave any later choice of theirs alone.
  const autoScoped = useRef(false)
  useEffect(() => {
    if (autoScoped.current || !result) return
    if (result.scope === 'mine' && result.personLinked && result.rows.length === 0) {
      autoScoped.current = true
      setScope('all')
    } else if (result.rows.length > 0) {
      autoScoped.current = true
    }
  }, [result])
  const rows: LiveFleetRow[] = useMemo(() => result?.rows ?? [], [result])

  const counts = useMemo(() => {
    const c: Record<LiveState, number> = { moving: 0, idling: 0, parked: 0, no_signal: 0, no_tracker: 0 }
    for (const r of rows) c[r.live_state] = (c[r.live_state] ?? 0) + 1
    return c
  }, [rows])

  const q = search.trim().toLowerCase()
  const visible = useMemo(
    () =>
      rows.filter(
        (r) =>
          (stateFilter === null || r.live_state === stateFilter) &&
          (q === '' ||
            (r.plate ?? '').toLowerCase().includes(q) ||
            (r.vehicle ?? '').toLowerCase().includes(q) ||
            (r.driver_names ?? '').toLowerCase().includes(q) ||
            (r.manager_name ?? '').toLowerCase().includes(q) ||
            (r.position_description ?? '').toLowerCase().includes(q)),
      ),
    [rows, stateFilter, q],
  )

  // Cars that can stream first, then by plate. Deliberately not by live state:
  // that changes on every 30 second refresh, and a grid that reshuffles moves
  // the picture a manager is watching out from under him.
  const cameraOrder = useMemo(
    () =>
      [...visible].sort(
        (a, b) =>
          Number(b.can_watch && canStream(b.camera_status)) - Number(a.can_watch && canStream(a.camera_status)) ||
          (a.plate ?? '').localeCompare(b.plate ?? ''),
      ),
    [visible],
  )
  const watchable = cameraOrder.filter((r) => r.can_watch && canStream(r.camera_status))
  const streaming = view === 'cameras' && tabVisible && !idlePaused && !userPaused
  // Any pause turns the sound back off, so resuming never starts a cabin
  // talking without the manager choosing it again.
  useEffect(() => {
    if (!streaming) setSoundVehicle(null)
  }, [streaming])

  const feedAt = sastTime(rows[0]?.feed_read_at ?? null)
  const onSelect = useCallback((id: string | null) => setSelected(id), [])

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            Live Fleet is for Fleetly staff. If you should be able to see it, ask an admin to give your
            login view access.
          </p>
        </div>
      </div>
    )
  }

  const notLinked = result && result.scope === 'mine' && !result.personLinked
  const managesNone = result && result.scope === 'mine' && result.personLinked && rows.length === 0

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <Navigation className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Live Fleet</h1>
          <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground tabular-nums">
            {feed.loading && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
            {feedAt ? `Cartrack read at ${feedAt}` : 'Waiting for the first Cartrack read'}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Positions come from Cartrack every minute and this page refreshes every 30 seconds.
        </p>

        <div className="mt-3 flex flex-wrap gap-2">
          {LIVE_STATES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStateFilter(stateFilter === s ? null : s)}
              className={cn('rounded-full', stateFilter === s && 'ring-2 ring-[hsl(var(--brand))]')}
            >
              <Chip tone={liveTone(s)}>
                {LIVE_STATE_LABEL[s]}
                <span className="ml-1 font-semibold tabular-nums">{counts[s]}</span>
              </Chip>
            </button>
          ))}
        </div>

        {feed.error && (
          <p className="mt-2 flex items-start gap-1.5 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              The live feed could not be read, so what is below may be out of date. It will try again in
              30 seconds. The server said: <span className="font-medium">{feed.error}</span>
            </span>
          </p>
        )}
        {notLinked && (
          <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Your login is not linked to a person, so the page cannot tell which cars are yours. Switch
              Show to Every car, or ask an admin to set your person on cockpit access.
            </span>
          </p>
        )}
        {managesNone && (
          <p className="mt-2 text-xs text-muted-foreground">
            No active car has you as its manager. Switch Show to Every car to see the whole fleet.
          </p>
        )}

        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="flex rounded-md border border-border p-0.5">
            {(['map', 'cameras'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={cn(
                  'flex items-center gap-1.5 rounded px-3 py-1 text-sm font-medium transition-colors',
                  view === v ? 'bg-[hsl(var(--brand))] text-white' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {v === 'map' ? <MapIcon className="h-4 w-4" /> : <Video className="h-4 w-4" />}
                {v === 'map' ? 'Map' : 'Cameras'}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="live-scope" className="text-xs">Show</Label>
            <NativeSelect
              id="live-scope"
              value={scope}
              onChange={(e) => {
                setScope(e.target.value as 'mine' | 'all')
                setSelected(null)
              }}
            >
              <option value="mine">My cars</option>
              <option value="all">Every car</option>
            </NativeSelect>
          </div>
          <div className="flex min-w-[220px] flex-1 flex-col gap-1">
            <Label htmlFor="live-search" className="text-xs">Search</Label>
            <Input
              id="live-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Registration, driver, manager or place"
            />
          </div>
        </div>
      </div>

      {view === 'cameras' ? (
        <div className="min-h-0 flex-1 overflow-auto p-3">
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>
              {watchable.length} of {visible.length} car{visible.length === 1 ? '' : 's'} can stream now. Sound is off until you
              turn it on for one car. Each camera uses about 270 MB an hour of that car{"'"}s data.
            </span>
            <button
              type="button"
              onClick={() => {
                if (streaming) setUserPaused(true)
                else {
                  setUserPaused(false)
                  setIdlePaused(false)
                  lastTouch.current = Date.now()
                }
              }}
              className="ml-auto flex items-center gap-1 rounded-md border border-border px-2 py-1 text-foreground hover:bg-muted"
            >
              {streaming ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              {streaming ? 'Pause all' : 'Resume'}
            </button>
          </div>
          {idlePaused && !userPaused && (
            <p className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
              The cameras paused after ten minutes without a touch, to save the cars{"'"} data. Press Resume to carry on watching.
            </p>
          )}
          {visible.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">No cars to show.</p>
          ) : (
            <div className="grid grid-flow-dense grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {cameraOrder.map((r, i) => (
                <CameraTile
                  key={r.vehicle_id}
                  vehicleId={r.vehicle_id}
                  plate={r.plate}
                  subtitle={r.driver_names ?? r.vehicle}
                  cameraStatus={r.camera_status ?? 'none'}
                  channels={Array.isArray(r.camera_channels) ? r.camera_channels : []}
                  canWatch={r.can_watch === true}
                  active={streaming}
                  startDelayMs={i * 1500}
                  soundOn={soundVehicle === r.vehicle_id}
                  onToggleSound={() => setSoundVehicle(soundVehicle === r.vehicle_id ? null : r.vehicle_id)}
                  large={largeVehicle === r.vehicle_id}
                  onToggleLarge={() => setLargeVehicle(largeVehicle === r.vehicle_id ? null : r.vehicle_id)}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <div className="h-[45vh] min-h-[280px] lg:h-auto lg:flex-1">
          <LiveFleetMap rows={visible} selectedVehicleId={selected} onSelectVehicle={onSelect} />
        </div>
        <div className="min-h-0 flex-1 overflow-auto border-t border-border lg:w-[380px] lg:flex-none lg:border-l lg:border-t-0">
          {feed.loading && rows.length === 0 ? (
            <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
              <Spinner className="h-4 w-4" /> Loading
            </div>
          ) : visible.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              {rows.length === 0 ? 'No cars to show.' : 'No car matches the filter.'}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {visible.map((r) => (
                <li key={r.vehicle_id}>
                  <button
                    type="button"
                    onClick={() => setSelected(r.vehicle_id === selected ? null : r.vehicle_id)}
                    className={cn(
                      'flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-muted/60',
                      r.vehicle_id === selected && 'bg-[hsl(var(--brand-soft))]',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-foreground">{r.plate ?? 'No plate'}</span>
                      <span className="truncate text-xs text-muted-foreground">{r.vehicle}</span>
                      <Chip tone={liveTone(r.live_state)} className="ml-auto">
                        {LIVE_STATE_LABEL[r.live_state]}
                      </Chip>
                    </div>
                    <span className="text-xs text-foreground">{statusLine(r)}</span>
                    {r.position_description && (
                      <span className="truncate text-xs text-muted-foreground">{r.position_description}</span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {r.driver_names ? `Seated: ${r.driver_names}` : 'No driver seated this week'}
                      {scope === 'all' && r.manager_name ? ` · ${r.manager_name}` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      )}
    </div>
  )
}
